import type { Socket, SocketOptions, ManagerOptions } from 'socket.io-client';

import { EventEmitter } from 'events';
import { io } from 'socket.io-client';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface WebSocketConfig {
  url: string;
  token?: string;
  tenantId?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  heartbeatTimeout?: number;
  maxQueueSize?: number;
}

export class WebSocketService extends EventEmitter {
  private socket: Socket | null = null;
  private config: Required<WebSocketConfig>;
  private status: ConnectionStatus = 'disconnected';
  private heartbeatTimer: any = null;
  public isPublic: boolean = false;

  constructor(config: WebSocketConfig) {
    super();
    this.config = {
      reconnectInterval: 1000,
      maxReconnectAttempts: 10,
      heartbeatInterval: 25000,
      heartbeatTimeout: 20000,
      maxQueueSize: 1000,
      token: '',
      tenantId: '',
      ...config,
    };

    // Prevent unhandled 'error' event crashes
    this.on('error', (err) => {
      // console.error('WebSocketService Error:', err);
    });
  }

  updateConfig(config: Partial<WebSocketConfig>): void {
    const tokenChanged = config.token && config.token !== this.config.token;
    const tenantChanged = config.tenantId && config.tenantId !== this.config.tenantId;

    this.config = { ...this.config, ...config };

    // Only reconnect if token or tenantId actually changed
    if (
      (tokenChanged || tenantChanged) &&
      (this.status === 'connected' || this.status === 'connecting' || this.status === 'error')
    ) {
      // console.log('🔄 WebSocket config changed, reconnecting...');
      this.disconnect();
      this.connect();
    }
  }

  connect(): void {
    // ⚠️ STRICT GUARD: NO CONNECTION without authentication
    if (!this.config.token || !this.config.tenantId) {
      console.warn('🚫 WebSocket connection BLOCKED - missing credentials');
      console.warn('⚠️ Socket will NOT connect until user logs in.');
      return;
    }

    this.isPublic = false;
    this._connect(this.config.token, this.config.tenantId);
  }

  // ✅ New method for Sign-Up flow ONLY
  connectPublic(): void {
    // console.log('🔓 Initiating Public WebSocket connection (for TENANT_PROVISIONED)');
    // Connect to public namespace (root) without token
    this.isPublic = true;
    this._connect(undefined, '');
  }

  private _connect(token: string | undefined, namespace: string): void {
    if (this.socket?.connected || this.status === 'connecting' || this.status === 'error') {
      // console.warn('⚠️ WebSocket already connecting, connected or in error state (retrying internally)');
      return;
    }

    this.setStatus('connecting');

    // Construct namespace URL
    const baseUrl = this.config.url.replace(/\/$/, '');
    const namespaceUrl = namespace ? `${baseUrl}/${namespace}` : baseUrl;

    //   console.log(`🔌 Connecting to ${token ? 'Authenticated' : 'Public'} WebSocket:`, namespaceUrl);

    // Socket.io options
    const options: Partial<ManagerOptions & SocketOptions> = {
      reconnection: true,
      reconnectionAttempts: this.config.maxReconnectAttempts,
      reconnectionDelay: this.config.reconnectInterval,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      timeout: 20000,
      transports: ['websocket'],
      secure: true,
      rejectUnauthorized: false,
      query: token ? { token } : undefined,
    };

    // Initialize socket
    this.socket = io(namespaceUrl, options);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      // console.log('✅ WebSocket connected', this.socket?.id);
      this.setStatus('connected');
      this.emit('connected');
      this.startHeartbeat();
    });

    this.socket.on('reconnect', (attempt) => {
      // console.log('🔌 WebSocket reconnected after %d attempts', attempt);
      this.setStatus('connected');
    });

    this.socket.on('reconnecting', (attempt) => {
      // console.log('🔌 WebSocket reconnecting (attempt %d)...', attempt);
      this.setStatus('connecting');
    });

    this.socket.on('disconnect', (reason) => {
      // console.log('🔌 WebSocket disconnected:', reason);
      this.stopHeartbeat();
      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        this.setStatus('disconnected');
      } else {
        // the disconnection was initiated by the client or lost connection
        // socket.io handles automatic reconnection if reconnection: true
        this.setStatus('disconnected');
      }
      this.emit('disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      // console.error('❌ WebSocket connection error:', error.message);
      this.setStatus('error');
      this.emit('error', { error });
    });

    // ✅ NEW: Notification event support
    this.socket.on('notification', (data) => {
      // console.log('🔔 Notification received', data);
      this.emit('notification', data);
    });

    // ✅ NEW: Message status event support
    this.socket.on('message.status', (data) => {
      // console.log('📝 Message status update:', data);
      this.emit('message.status', data);
    });

    // ✅ NEW: Legacy event handlers for backward compatibility
    // These events were previously handled in call-provider.tsx
    const legacyEvents = [
      'message.upsert',
      'conversation.upsert',
      // 'email.upsert',
      // 'livechatmessage.upsert',
      // 'messengermessage.upsert',
      // 'instagrammessage.upsert',
      // 'smsmessage.upsert',
      // 'linemessage.upsert',
      // 'telegrammessage.upsert',
      // 'msteamsmessage.upsert',
      'template.sync',
    ];

    legacyEvents.forEach((eventName) => {
      this.socket!.on(eventName, (data) => {
        // console.log(`📨 Legacy event received: ${eventName}`, data);
        this.emit(eventName, data);
      });
    });

    // Existing message.new event handler
    this.socket.on('message.new', (data) => {
      // console.log('📩 New message received:', data);
      this.emit('message.new', data);
    });

    // Existing conversation events
    this.socket.on('conversation.read', (data) => {
      // console.log('👁️ Conversation read:', data);
      this.emit('conversation.read', data);
    });

    // Mirror all incoming events to the EventEmitter
    this.socket.onAny((event, ...args) => {
      // console.log('📥 Socket Event:', event, args);
      this.emit(event, ...args);

      const data = args.length === 1 ? args[0] : args;
      this.emit('message', { type: event, payload: data });
    });
  }

  send(data: any): void {
    if (!this.socket) {
      console.warn('⚠️ WebSocket not initialized. Call connect() first.');
      return;
    }

    if (typeof data === 'object' && data !== null && 'type' in data) {
      const { type, payload, ...rest } = data;
      this.socket.emit(type, payload ?? rest);
    } else {
      console.warn('⚠️ Send format expected: { type: string, payload: any }');
      // this.socket.send(data); // Socket.io typically uses emit(event, data)
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.connected) {
        // console.log('💓 Sending heartbeat ping');
        this.socket.emit('ping');
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Subscribe to a specific conversation room
   */
  subscribeToConversation(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('conversation.subscribe', conversationId);
  }

  /**
   * Subscribe to tenant room
   */
  subscribeToTenant(tenantId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('subscribe', { type: 'tenant', id: tenantId });
  }

  /**
   * Unsubscribe from a specific conversation room
   */
  unsubscribeFromConversation(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('conversation.unsubscribe', conversationId);
  }

  /**
   * Mark conversation as read
   */
  markConversationAsRead(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('conversation.read', { conversationId });
  }

  private setStatus(status: ConnectionStatus): void {
    this.status = status;
    this.emit('status_change', status);
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  disconnect(): void {
    // console.log('🔌 Disconnecting WebSocket');
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isPublic = false;
    this.setStatus('disconnected');
  }

  subscribe(eventType: string, handler: (...args: any[]) => void): () => void {
    this.on(eventType, handler);
    return () => this.off(eventType, handler);
  }

  getQueueStats() {
    return {
      size: this.socket?.connected ? 0 : 'unknown',
      maxSize: this.config.maxQueueSize,
      utilizationPercent: 0,
    };
  }
}
